import { useState, useRef, useMemo, useEffect } from "react";
import { useParams, Link } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { MigrationStepper } from "./components/MigrationStepper";
import { StepFooter } from "./components/StepFooter";
import { FileHeaderCard } from "./components/FileHeaderCard";
import { DoneStep } from "./components/DoneStep";
import { RosterOnlyTable, type RosterOnlyRow } from "./components/RosterOnlyTable";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import PlansStep from "./steps/PlansStep";
import { ColumnMapper, type InternalField } from "@/components/migration/ColumnMapper";
import { EmployeeImport } from "@/components/employees/EmployeeImport";
import {
  EditEmployeeModal,
  type EditableEmployee,
} from "@/components/migration/EditEmployeeModal";
import {
  EditDependentModal,
  type EditableDependent,
} from "@/components/migration/EditDependentModal";
import {
  EditEnrollmentModal,
  type EditableEnrollment,
} from "@/components/migration/EditEnrollmentModal";
import { apiRequest } from "@/lib/queryClient";
import { US_STATES } from "@/lib/usStates";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Clock,
  Eye,
  Upload,
  FileSpreadsheet,
  Loader2,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Users,
  Search,
  X,
} from "lucide-react";

type MigrationStepStatus =
  | "pending"
  | "uploaded"
  | "previewing"
  | "confirmed"
  | "skipped"
  | "error";
type MigrationJobStatus = "not_started" | "in_progress" | "complete";

interface MigrationJob {
  id: number;
  clientId: number | null;
  clientName: string;
  sourcePlatform: string;
  status: MigrationJobStatus;
  currentStep: string;
  rosterStatus: MigrationStepStatus;
  rosterFileName: string | null;
  rosterRawData: any;
  rosterData: any;
  rosterValidationErrors: any;
  censusStatus: MigrationStepStatus;
  plansStatus: MigrationStepStatus;
  enrollmentsStatus: MigrationStepStatus;
  censusData: any;
  censusRawData: any;
  censusColumnMapping: any;
  censusValidationErrors: any;
  plansData: any;
  enrollmentsData: any;
  censusFileName: string | null;
  enrollmentsFileName: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  internalFields?: InternalField[];
}

interface UploadResponse {
  job: MigrationJob;
  summary: {
    carriers: number;
    plans: number;
    employees: number;
    dependents: number;
    enrollments: number;
    warnings: number;
    warningsByCode: Record<string, number>;
  };
}

const PREVIEW_LIMIT = 25;

function maskSsn(ssn: string | null | undefined): string {
  if (!ssn) return "—";
  const digits = ssn.replace(/\D/g, "");
  if (digits.length < 4) return "—";
  return `***-**-${digits.slice(-4)}`;
}

function fmt(value: unknown): string {
  if (value == null || value === "") return "—";
  return String(value);
}

function empKeyFromEmployee(emp: any): string {
  if (emp?.eid) return `eid:${emp.eid}`;
  return `name:${String(emp?.firstName ?? "").toLowerCase().trim()}|${String(emp?.lastName ?? "").toLowerCase().trim()}`;
}

function empKeyFromDep(dep: any): string {
  if (dep?.employeeEid) return `eid:${dep.employeeEid}`;
  return `name:${String(dep?.employeeFirstName ?? "").toLowerCase().trim()}|${String(dep?.employeeLastName ?? "").toLowerCase().trim()}`;
}

function PreviewToggle({
  total,
  shown,
  expanded,
  onToggle,
}: {
  total: number;
  shown: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  if (total <= PREVIEW_LIMIT) return null;
  const remaining = total - shown;
  return (
    <div className="mt-3 flex items-center justify-between rounded-md border border-dashed bg-muted/20 px-4 py-2.5 text-xs">
      <span className="text-muted-foreground">
        Showing{" "}
        <span className="font-medium text-foreground">{shown}</span> of{" "}
        <span className="font-medium text-foreground">{total}</span>
      </span>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 gap-1 text-xs"
        onClick={onToggle}
      >
        {expanded ? (
          <>
            <ChevronUp className="h-3 w-3" />
            Show fewer
          </>
        ) : (
          <>
            <ChevronDown className="h-3 w-3" />
            Show {remaining} more
          </>
        )}
      </Button>
    </div>
  );
}

type ParserWarning = {
  rowNumber: number | null;
  severity: "info" | "warn" | "error";
  code: string;
  message: string;
};

// Warning codes that block the whole census import, as opposed to row-level
// errors that just drop the offending row. Add new codes here when other
// import-blocking checks land (e.g. missing required field across all rows).
const BLOCKING_CODES = new Set<string>(["MISSING_EMAIL"]);

// Friendly per-code titles. Falls back to a Title-Cased snake_case rendering
// for unknown codes — better than showing UPPER_SNAKE to brokers.
const WARNING_CODE_TITLES: Record<string, string> = {
  MISSING_EMAIL: "Email missing",
  MISSING_DOB: "Date of birth missing",
  MISSING_SSN: "SSN missing",
  MISSING_HIRE_DATE: "Hire date missing",
  COMPENSATION_CONFLICT: "Compensation type conflict",
  STATUS_FROM_ACTION: "Status inferred from action",
  MID_YEAR_ENROLLMENT: "Mid-year enrollment",
  AGE_RATED_PLAN: "Rates likely age-rated",
  UNKNOWN_PLAN_TYPE: "Plan type not recognized",
};

function titleForCode(code: string): string {
  if (WARNING_CODE_TITLES[code]) return WARNING_CODE_TITLES[code];
  return code
    .toLowerCase()
    .split("_")
    .map((s, i) => (i === 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s))
    .join(" ");
}

// Auto-handled by the parser. Surfaced behind a single collapsible footer so
// they don't drown out anything the manager actually needs to look at.
// Everything NOT in this set is treated as actionable by default — so new
// parser warning codes show up to brokers until someone explicitly demotes
// them here.
const AUTO_HANDLED_CODES = new Set<string>([
  "STATUS_FROM_ACTION",
  "COMPENSATION_CONFLICT",
  "DEPENDENT_NAME_UNKNOWN",
  "CARRIER_NAME_VARIANT",
  "DUPLICATE_EMPLOYEE",
]);

// One-line human-readable summary per code (replaces N repeated per-row messages).
const CODE_SUMMARIES: Record<string, (n: number) => string> = {
  MID_YEAR_EVENT: (n) => `${n} mid-year enrollment ${n === 1 ? "change" : "changes"} — confirm each falls within a QLE window or new-hire window.`,
  MID_YEAR_ENROLLMENT: (n) => `${n} mid-year enrollment ${n === 1 ? "change" : "changes"} — confirm each falls within a QLE window or new-hire window.`,
  STATUS_FROM_ACTION: (n) => `${n} ${n === 1 ? "election" : "elections"} had a dollar amount in the Status column (Life/AD&D, FSA, etc.); coverage amounts captured separately.`,
  COMPENSATION_CONFLICT: (n) => `${n} ${n === 1 ? "employee" : "employees"} had conflicting compensation values across rows; the first one was kept.`,
  DEPENDENT_NAME_UNKNOWN: (n) => `${n} dependent ${n === 1 ? "row" : "rows"} reused the employee's name (file format limitation); reconcile after import.`,
  CARRIER_NAME_VARIANT: (n) => `${n} carrier name spelling ${n === 1 ? "variant" : "variants"} normalized to a canonical form.`,
  DUPLICATE_EMPLOYEE: (n) => `${n} duplicate employee ${n === 1 ? "row" : "rows"} found; the first occurrence of each was kept.`,
  ENROLLED_WITHOUT_PLAN_NAME: (n) => `${n} ${n === 1 ? "row" : "rows"} marked Enrolled without a plan name — verify the plan assignment.`,
  UNKNOWN_PLAN_TYPE: (n) => `${n} ${n === 1 ? "row" : "rows"} reference a plan type we don't recognize; these will be skipped.`,
  UNSUPPORTED_PLAN_TYPE: (n) => `${n} ${n === 1 ? "row" : "rows"} reference a plan type not yet supported by the schema; review before import.`,
  MISSING_DOB: (n) => `${n} ${n === 1 ? "employee is" : "employees are"} missing a date of birth.`,
  MISSING_SSN: (n) => `${n} ${n === 1 ? "employee is" : "employees are"} missing an SSN.`,
  MISSING_HIRE_DATE: (n) => `${n} ${n === 1 ? "employee is" : "employees are"} missing a hire date.`,
  AGE_RATED_PLAN: (n) => `${n} ${n === 1 ? "plan looks" : "plans look"} age-rated; verify rate tables after import.`,
  ORPHAN_DEPENDENT: (n) => `${n} dependent ${n === 1 ? "row" : "rows"} couldn't be linked to an employee; will be skipped.`,
};

function summaryForCode(code: string, count: number): string {
  const fn = CODE_SUMMARIES[code];
  if (fn) return fn(count);
  return `${count} ${titleForCode(code).toLowerCase()} ${count === 1 ? "row" : "rows"}.`;
}

/** Join warning rowNumber → enrollment row context so the expanded detail shows names, not row indices. */
function rowContextFor(
  rowNumber: number | null | undefined,
  enrollments: any[] | undefined,
): { name: string; plan: string | null; effectiveDate: string | null; tier: string | null } | null {
  if (rowNumber == null || !Array.isArray(enrollments)) return null;
  const e = enrollments.find((r) => r?.rowNumber === rowNumber);
  if (!e) return null;
  const first = e.ownerEmployeeFirstName ?? e.personFirstName ?? "";
  const last = e.ownerEmployeeLastName ?? e.personLastName ?? "";
  const name = `${first} ${last}`.trim() || "(unnamed)";
  return {
    name,
    plan: e.planDisplayName ?? null,
    effectiveDate: e.effectiveDate ?? null,
    tier: e.coverageTier ?? null,
  };
}

function WarningsPanel({
  title,
  warnings,
  filter,
  onAcknowledgeAll,
  enrollments,
}: {
  title?: string;
  warnings: ParserWarning[];
  filter?: (w: ParserWarning) => boolean;
  onAcknowledgeAll?: () => void;
  /** Optional join source for row-level enrichment. Pass `job.enrollmentsData?.enrollments`. */
  enrollments?: any[];
}) {
  const [expandedCodes, setExpandedCodes] = useState<Set<string>>(new Set());
  const [dismissedCodes, setDismissedCodes] = useState<Set<string>>(new Set());
  const [autoBucketOpen, setAutoBucketOpen] = useState(false);

  const list = filter ? warnings.filter(filter) : warnings;
  if (list.length === 0) return null;

  const byCode = list.reduce<Record<string, ParserWarning[]>>((acc, w) => {
    (acc[w.code] ??= []).push(w);
    return acc;
  }, {});
  const allCodes = Object.keys(byCode).sort(
    (a, b) => byCode[b].length - byCode[a].length,
  );
  const visibleCodes = allCodes.filter((c) => !dismissedCodes.has(c));
  const actionableCodes = visibleCodes.filter(
    (c) => !AUTO_HANDLED_CODES.has(c),
  );
  const autoCodes = visibleCodes.filter((c) => AUTO_HANDLED_CODES.has(c));
  const visibleTotal = visibleCodes.reduce((s, c) => s + byCode[c].length, 0);
  const autoTotal = autoCodes.reduce((s, c) => s + byCode[c].length, 0);

  if (visibleCodes.length === 0) {
    return (
      <Alert className="border-emerald-200 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-900/20">
        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        <AlertTitle className="text-emerald-800 dark:text-emerald-300">
          All warnings accepted
        </AlertTitle>
        <AlertDescription className="text-emerald-800/80 dark:text-emerald-300/80">
          {list.length} parser warning{list.length === 1 ? "" : "s"} reviewed
          and dismissed.
        </AlertDescription>
      </Alert>
    );
  }

  const toggle = (code: string) => {
    setExpandedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const acceptGroup = (code: string) => {
    setDismissedCodes((prev) => {
      const next = new Set(prev);
      next.add(code);
      const remaining = allCodes.filter((c) => !next.has(c));
      if (remaining.length === 0) onAcknowledgeAll?.();
      return next;
    });
  };

  const acceptAll = () => {
    setDismissedCodes(new Set(allCodes));
    onAcknowledgeAll?.();
  };

  const headline =
    title ?? `${visibleTotal} parser warning${visibleTotal === 1 ? "" : "s"}`;

  const renderCodeGroup = (code: string) => {
    const rows = byCode[code];
    const isOpen = expandedCodes.has(code);
    const severity = rows[0]?.severity ?? "warn";
    const pillClass =
      severity === "error"
        ? "border-red-300 bg-red-50 text-red-800"
        : severity === "info"
          ? "border-blue-300 bg-blue-50 text-blue-800"
          : "border-amber-300 bg-amber-100/80 text-amber-900";
    return (
      <div
        key={code}
        className="rounded-md border border-amber-200/80 bg-white dark:bg-zinc-900"
      >
        <div
          role="button"
          tabIndex={0}
          onClick={() => toggle(code)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggle(code);
            }
          }}
          className="flex w-full cursor-pointer items-start gap-2 px-3 py-2 text-left"
        >
          <ChevronRight
            className={`mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${
              isOpen ? "rotate-90" : ""
            }`}
          />
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className={`font-mono text-[10.5px] ${pillClass}`}
              >
                {code}
              </Badge>
              <span className="text-sm font-medium">{titleForCode(code)}</span>
              <span className="text-xs text-muted-foreground">
                · {rows.length} row{rows.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="text-xs text-stone-700 dark:text-stone-300">
              {summaryForCode(code, rows.length)}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 text-xs text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 dark:text-emerald-400"
            onClick={(e) => {
              e.stopPropagation();
              acceptGroup(code);
            }}
          >
            <CheckCircle2 className="mr-1 h-3 w-3" />
            Accept
          </Button>
        </div>
        {isOpen && (
          <div className="border-t border-amber-100 px-3 py-3">
            <ul className="max-h-60 space-y-1 overflow-y-auto rounded-md border bg-muted/40 p-2 text-[11.5px] leading-relaxed">
              {rows.slice(0, 200).map((w, i) => {
                const ctx = rowContextFor(w.rowNumber, enrollments);
                return (
                  <li key={i} className="flex flex-wrap gap-x-2">
                    <span className="font-mono text-muted-foreground shrink-0">
                      row {w.rowNumber ?? "?"}
                    </span>
                    {ctx ? (
                      <span>
                        <span className="font-medium">{ctx.name}</span>
                        {ctx.plan && (
                          <span className="text-stone-500"> · {ctx.plan}</span>
                        )}
                        {ctx.effectiveDate && (
                          <span className="text-stone-500">
                            {" "}
                            · eff. {ctx.effectiveDate}
                          </span>
                        )}
                        {ctx.tier && (
                          <span className="text-stone-500">
                            {" "}
                            · {String(ctx.tier).replace(/_/g, " ")}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="font-mono">{w.message}</span>
                    )}
                  </li>
                );
              })}
              {rows.length > 200 && (
                <li className="italic text-muted-foreground">
                  … and {rows.length - 200} more
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/20">
      <div className="flex items-center gap-2 px-4 py-3">
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
        <span className="text-sm font-medium text-amber-900 dark:text-amber-200">
          {headline}
        </span>
        <span className="text-xs text-amber-800/70 dark:text-amber-300/70">
          · grouped into {visibleCodes.length} type
          {visibleCodes.length === 1 ? "" : "s"}
        </span>
        <div className="flex-1" />
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-amber-900 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-900/40"
          onClick={acceptAll}
        >
          Accept all
        </Button>
      </div>
      <div className="space-y-1.5 px-2 pb-2">
        {actionableCodes.length === 0 && autoCodes.length > 0 && (
          <div className="px-2 py-2 text-xs italic text-stone-600 dark:text-stone-400">
            No items needing review. {autoTotal} auto-handled detail
            {autoTotal === 1 ? "" : "s"} below.
          </div>
        )}
        {actionableCodes.map(renderCodeGroup)}
        {autoCodes.length > 0 && (
          <div className="rounded-md border border-stone-200 bg-stone-50 dark:border-stone-700 dark:bg-stone-900/40">
            <button
              type="button"
              onClick={() => setAutoBucketOpen((v) => !v)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-stone-700 dark:text-stone-300"
            >
              <ChevronRight
                className={`h-3.5 w-3.5 transition-transform ${
                  autoBucketOpen ? "rotate-90" : ""
                }`}
              />
              <span className="font-medium">Auto-handled details</span>
              <span className="text-stone-500">
                · {autoTotal} row{autoTotal === 1 ? "" : "s"} across{" "}
                {autoCodes.length} code{autoCodes.length === 1 ? "" : "s"}
              </span>
              <div className="flex-1" />
              <span className="text-stone-500">
                {autoBucketOpen ? "Hide" : "Show"}
              </span>
            </button>
            {autoBucketOpen && (
              <div className="space-y-1.5 border-t border-stone-200 px-2 py-2 dark:border-stone-700">
                {autoCodes.map(renderCodeGroup)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Inline panel that renders the most-recent confirm-step result's `errors`
 * array. Used for census/plans/enrollments confirm results so warnings stay
 * readable instead of getting truncated in a toast.
 */
function ConfirmIssuesPanel({
  step,
  result,
}: {
  step: "census" | "plans" | "enrollments";
  result: Record<string, unknown> | undefined;
}) {
  const [expanded, setExpanded] = useState(true);
  const errs = result?.errors;
  if (!Array.isArray(errs) || errs.length === 0) return null;
  const lines = errs as string[];
  return (
    <Alert className="mt-4 border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/20">
      <AlertTriangle className="h-4 w-4 text-amber-600" />
      <AlertTitle className="text-amber-800 dark:text-amber-300">
        {lines.length} issue{lines.length === 1 ? "" : "s"} from last {step}{" "}
        confirm
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="ml-2 text-xs font-normal underline"
        >
          {expanded ? "Hide" : "Show"}
        </button>
      </AlertTitle>
      {expanded && (
        <AlertDescription className="text-amber-800 dark:text-amber-300">
          <ul className="mt-2 max-h-72 space-y-1 overflow-y-auto pl-4 text-xs">
            {lines.map((line, i) => (
              <li key={i} className="list-disc">
                {line}
              </li>
            ))}
          </ul>
        </AlertDescription>
      )}
    </Alert>
  );
}

function StepStatusBadge({ status }: { status: MigrationStepStatus }) {
  if (status === "confirmed") {
    return (
      <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300">
        <CheckCircle2 className="mr-1 h-3 w-3" /> Confirmed
      </Badge>
    );
  }
  if (status === "skipped") {
    return (
      <Badge variant="outline" className="text-muted-foreground">
        Skipped
      </Badge>
    );
  }
  if (status === "previewing") {
    return (
      <Badge className="bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300">
        <Clock className="mr-1 h-3 w-3" /> Ready to review
      </Badge>
    );
  }
  if (status === "error") {
    return (
      <Badge variant="destructive">
        <AlertCircle className="mr-1 h-3 w-3" /> Error
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-muted-foreground">
      Pending
    </Badge>
  );
}

export default function ClientMigration() {
  const params = useParams<{ id: string }>();
  const jobId = Number(params.id);
  // Wouter doesn't support regex param constraints, so /migration/import
  // double-matches /migration/:id with id="import" → NaN. Suppress the broken
  // "job #NaN does not exist" render; BulkClientImport handles that route.
  if (!Number.isFinite(jobId) || jobId <= 0) return null;
  const { toast } = useToast();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const rosterFileInputRef = useRef<HTMLInputElement>(null);
  const [rosterDragOver, setRosterDragOver] = useState(false);
  const [uploadSummary, setUploadSummary] = useState<UploadResponse["summary"] | null>(null);
  const [showAllEmployees, setShowAllEmployees] = useState(false);
  const [showAllEnrollments, setShowAllEnrollments] = useState(false);
  const [enrollmentStatusFilter, setEnrollmentStatusFilter] = useState<
    "enrolled" | "waived" | "terminated" | null
  >(null);
  const [enrollmentPersonFilter, setEnrollmentPersonFilter] = useState<
    "all" | "employee" | "dependent"
  >("all");
  const [enrollmentPlanFilter, setEnrollmentPlanFilter] = useState<string>("all");
  const [enrollmentSearch, setEnrollmentSearch] = useState("");
  const [expandedEmployees, setExpandedEmployees] = useState<Set<string>>(
    new Set(),
  );
  const [activeTab, setActiveTab] = useState<
    "roster" | "census" | "plans" | "enrollments" | "done"
  >("roster");
  const [dragOverTarget, setDragOverTarget] = useState<null | "upload">(null);
  const [editingEmployeeIdx, setEditingEmployeeIdx] = useState<number | null>(
    null,
  );
  const [editingRosterEmployeeIdx, setEditingRosterEmployeeIdx] = useState<
    number | null
  >(null);
  const [editingDependentIdx, setEditingDependentIdx] = useState<number | null>(
    null,
  );
  const [editingEnrollmentIdx, setEditingEnrollmentIdx] = useState<
    number | null
  >(null);
  // PlansStep owns the in-card gate (per-plan validation, edit-in-progress,
  // age-rated warnings). It bubbles its readiness up so the footer Continue
  // can drive confirm without re-implementing those rules here.
  const [plansGate, setPlansGate] = useState<{
    ready: boolean;
    reason: string;
  }>({ ready: false, reason: "" });

  const { data: job, isLoading } = useQuery<MigrationJob>({
    queryKey: [`/api/migration/jobs/${jobId}`],
    enabled: Number.isFinite(jobId) && jobId > 0,
  });

  // Pulls the client record so we know the home state. Drives the Step 3
  // "Rating region" dropdown (CA-only seeded today; plain text fallback
  // elsewhere) — see PlansStep. Normalize whatever's stored ("California",
  // "ca", "CA") into a 2-letter postal code so the rating-areas endpoint
  // matches.
  const { data: clientRecord } = useQuery<{ state?: string | null }>({
    queryKey: [`/api/clients/${job?.clientId}`],
    enabled: Boolean(job?.clientId),
  });
  const clientState = useMemo(() => {
    const raw = clientRecord?.state?.trim();
    if (!raw) return null;
    if (raw.length === 2) return raw.toUpperCase();
    const match = US_STATES.find(
      (s) => s.name.toLowerCase() === raw.toLowerCase(),
    );
    return match?.code ?? null;
  }, [clientRecord?.state]);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`/api/migration/jobs/${jobId}/upload`, {
        method: "POST",
        body: form,
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message ?? `Upload failed (${res.status})`);
      }
      return (await res.json()) as UploadResponse;
    },
    onSuccess: (data) => {
      setUploadSummary(data.summary);
      qc.invalidateQueries({ queryKey: [`/api/migration/jobs/${jobId}`] });
      toast({
        title: "File parsed",
        description: `${data.summary.employees} employees, ${data.summary.plans} plans, ${data.summary.enrollments} enrollments.`,
      });
    },
    onError: (err: any) => {
      toast({
        title: "Upload failed",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      });
    },
  });

  const makeConfirmMutationOpts = (
    step: "census" | "plans" | "enrollments",
    onAdvance?: () => void,
  ) => ({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/migration/jobs/${jobId}/${step}/confirm`,
      );
      return (await res.json()) as Record<string, unknown>;
    },
    onSuccess: (data: Record<string, unknown>) => {
      qc.invalidateQueries({ queryKey: [`/api/migration/jobs/${jobId}`] });
      // Build a compact toast summary from scalar fields only (skip arrays
      // like `errors`/`skippedRatesPlanIds` — those render inline below).
      const summary = Object.entries(data)
        .filter(([k, v]) => k !== "errors" && !Array.isArray(v))
        .map(([k, v]) => `${k}: ${v}`)
        .join(", ");
      const errs = data.errors;
      const errCount = Array.isArray(errs) ? errs.length : 0;
      toast({
        title: `${step} confirmed`,
        description:
          errCount > 0
            ? `${summary}${summary ? " · " : ""}${errCount} warning${errCount === 1 ? "" : "s"} (see panel below)`
            : summary,
      });
      onAdvance?.();
    },
    onError: (err: any) => {
      toast({
        title: `Failed to confirm ${step}`,
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      });
    },
  });

  const confirmCensus = useMutation(
    makeConfirmMutationOpts("census", () => setActiveTab("plans")),
  );
  const confirmPlans = useMutation(
    makeConfirmMutationOpts("plans", () => setActiveTab("enrollments")),
  );
  const confirmEnrollments = useMutation(
    makeConfirmMutationOpts("enrollments", () => setActiveTab("done")),
  );

  // Roster step (Phase 2 Step 1) — dedicated Full Census parser.
  // Upload stages employees + dependents in jsonb; confirm writes them to
  // the live DB via the same confirmCensus helper used by Step 2.
  const rosterUpload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(
        `/api/migration/jobs/${jobId}/roster/upload`,
        { method: "POST", body: form, credentials: "include" },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message ?? `Upload failed (${res.status})`);
      }
      return (await res.json()) as {
        job: MigrationJob;
        summary: { employees: number; dependents: number; warnings: number };
      };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: [`/api/migration/jobs/${jobId}`] });
      toast({
        title: "Full census parsed",
        description: `${data.summary.employees} employees, ${data.summary.dependents} dependents (${data.summary.warnings} warnings).`,
      });
    },
    onError: (err: any) =>
      toast({
        title: "Roster upload failed",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      }),
  });

  const confirmRoster = useMutation({
    mutationFn: async (skipped: boolean) => {
      const res = await apiRequest(
        "POST",
        `/api/migration/jobs/${jobId}/roster/confirm`,
        { skipped },
      );
      return (await res.json()) as {
        job: MigrationJob;
        result?: {
          createdEmployeeCount: number;
          updatedEmployeeCount: number;
          createdDependentCount: number;
        };
      };
    },
    onSuccess: (data, skipped) => {
      qc.invalidateQueries({ queryKey: [`/api/migration/jobs/${jobId}`] });
      qc.invalidateQueries({
        queryKey: ["/api/employees", { clientId: job?.clientId }],
      });
      const r = data.result;
      toast({
        title: skipped ? "Roster step skipped" : "Roster confirmed",
        description: skipped
          ? "Step 2 will reconcile employees against the existing roster."
          : r
            ? `${r.createdEmployeeCount} new + ${r.updatedEmployeeCount} updated employees, ${r.createdDependentCount} dependents.`
            : "Employees loaded.",
      });
      setActiveTab("census");
    },
    onError: (err: any) =>
      toast({
        title: "Failed to confirm roster",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      }),
  });

  // Current roster size — informs the broker whether they should skip Step 1.
  const rosterCount = useQuery<{ length: number } | unknown[]>({
    queryKey: ["/api/employees", { clientId: job?.clientId }],
    queryFn: async () => {
      const res = await fetch(
        `/api/employees?clientId=${job?.clientId}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`Failed to load employees (${res.status})`);
      return res.json();
    },
    enabled: Boolean(job?.clientId),
  });
  const currentRosterCount = Array.isArray(rosterCount.data)
    ? rosterCount.data.length
    : 0;

  // Auto-advance tab when the server reports a step change (e.g. after
  // confirm). Doesn't prevent the user from clicking back to an earlier tab.
  useEffect(() => {
    if (!job) return;
    if (job.currentStep === "done") setActiveTab("done");
    else if (job.currentStep === "plans") setActiveTab("plans");
    else if (job.currentStep === "enrollments") setActiveTab("enrollments");
    else if (job.currentStep === "census") setActiveTab("census");
    else if (job.currentStep === "roster") setActiveTab("roster");
  }, [job?.currentStep]);

  // Parser warnings stored at upload time on censusValidationErrors.
  const parserWarnings = useMemo<ParserWarning[]>(() => {
    const raw = job?.censusValidationErrors;
    if (Array.isArray(raw)) return raw as ParserWarning[];
    return [];
  }, [job?.censusValidationErrors]);

  // Blockers = warning codes that prevent the entire census import (vs.
  // row-skipping warnings that just drop a row). Today only MISSING_EMAIL.
  const blockers = useMemo(
    () => parserWarnings.filter((w) => BLOCKING_CODES.has(w.code)),
    [parserWarnings],
  );

  // Group deps by their parent employee key so we can render them nested.
  const { depsByEmployee, orphanDeps } = useMemo(() => {
    const raw = (job?.censusData ?? {}) as { employees?: any[]; dependents?: any[] };
    const employeeKeys = new Set<string>(
      (raw.employees ?? []).map((e) => empKeyFromEmployee(e)),
    );
    const byEmp = new Map<string, any[]>();
    const orphans: any[] = [];
    for (const d of raw.dependents ?? []) {
      const key = empKeyFromDep(d);
      if (!employeeKeys.has(key)) {
        orphans.push(d);
        continue;
      }
      const list = byEmp.get(key);
      if (list) list.push(d);
      else byEmp.set(key, [d]);
    }
    return { depsByEmployee: byEmp, orphanDeps: orphans };
  }, [job?.censusData]);

  // Step 1 ↔ Step 2 coverage diff. Tells brokers which roster employees are
  // also in the enrollment census ("enrolled" or "waived") vs unique to Step
  // 1 ("roster-only"). Drives the CoverageCheck bar and the RosterOnlyTable.
  // Computes from the two staged jsonb blobs — no new endpoint.
  const coverage = useMemo(() => {
    const rosterEmployees =
      ((job?.rosterData as { employees?: any[] } | null)?.employees ?? []) as any[];
    const censusEmployees =
      ((job?.censusData as { employees?: any[] } | null)?.employees ?? []) as any[];
    const censusEnrollments =
      ((job?.enrollmentsData as { enrollments?: any[] } | null)?.enrollments ??
        []) as any[];

    const censusKeys = new Set<string>(
      censusEmployees.map((e) => empKeyFromEmployee(e)),
    );
    // Employees with at least one non-waived enrollment.
    const enrolledKeys = new Set<string>();
    for (const enr of censusEnrollments) {
      const status = String(enr.status ?? "").toLowerCase();
      if (status === "enrolled") {
        // Match enrollment back to its owner employee. ParsedEnrollment carries
        // ownerEmployeeFirstName/LastName (and often ownerEmployeeEid). Fake an
        // employee shape to reuse empKeyFromEmployee.
        const fake = {
          eid: enr.ownerEmployeeEid ?? null,
          firstName: enr.ownerEmployeeFirstName ?? "",
          lastName: enr.ownerEmployeeLastName ?? "",
        };
        enrolledKeys.add(empKeyFromEmployee(fake));
      }
    }

    let enrolled = 0;
    let waived = 0;
    const rosterOnly: Array<{ rosterIdx: number; emp: any }> = [];
    const sourceByKey = new Map<string, "both" | "roster_only">();

    rosterEmployees.forEach((e, idx) => {
      const key = empKeyFromEmployee(e);
      if (censusKeys.has(key)) {
        sourceByKey.set(key, "both");
        if (enrolledKeys.has(key)) enrolled++;
        else waived++;
      } else {
        sourceByKey.set(key, "roster_only");
        rosterOnly.push({ rosterIdx: idx, emp: e });
      }
    });

    const total = rosterEmployees.length;
    const diffComputed = censusEmployees.length > 0;

    return {
      total,
      enrolled,
      waived,
      rosterOnly: rosterOnly.length,
      rosterOnlyPeople: rosterOnly,
      sourceByKey,
      diffComputed,
    };
  }, [job?.rosterData, job?.censusData, job?.enrollmentsData]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!job) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Migration job not found</AlertTitle>
        <AlertDescription>
          The job #{jobId} does not exist or you don't have access.{" "}
          <Link href="/migration/hub" className="underline">
            Back to Migration Hub
          </Link>
        </AlertDescription>
      </Alert>
    );
  }

  const census = (job.censusData ?? {}) as {
    employees?: any[];
    dependents?: any[];
  };
  const plansBlob = (job.plansData ?? {}) as {
    carriers?: any[];
    plans?: any[];
  };
  const enrollmentsBlob = (job.enrollmentsData ?? {}) as {
    enrollments?: any[];
  };

  // "Confirmable" means: the prior step is done (or N/A) AND this step isn't
  // already confirmed. The backend rejects invalid states; we don't need a
  // strict `previewing`-only check here, which was too narrow and disabled
  // the footer Continue when the status was `uploaded`, `pending`, etc.
  const canConfirmCensus =
    !!job.censusFileName && job.censusStatus !== "confirmed";
  const canConfirmPlans =
    job.censusStatus === "confirmed" && job.plansStatus !== "confirmed";
  const canConfirmEnrollments =
    job.plansStatus === "confirmed" && job.enrollmentsStatus !== "confirmed";
  // Wizard is fully done once enrollments confirms. Edits to staging blobs
  // for plans and enrollments wouldn't propagate to live records, and edits
  // to census employees, while they DO sync, belong on the client profile
  // post-completion. Lock all in-wizard editors when this flips true.
  const isWizardDone = job.enrollmentsStatus === "confirmed";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link href="/migration/hub" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline">
            <ArrowLeft className="h-3 w-3" />
            Migration Hub
          </Link>
          <h1 className="mt-1 text-2xl font-bold">{job.clientName}</h1>
          <p className="text-sm text-muted-foreground">
            Migration from <span className="font-medium">{job.sourcePlatform}</span> • Job #{job.id}
          </p>
        </div>
        <Badge variant={job.status === "complete" ? "default" : "outline"}>
          {job.status === "complete"
            ? "Complete"
            : job.status === "in_progress"
              ? "In progress"
              : "Not started"}
        </Badge>
      </div>

      {/* Stepper is always visible — including on the success view, so
          brokers can bounce between any step body and the Summary card.
          The 5th chip is "Summary" (not "Done") so it reads as a recap
          destination, not a task to complete. */}
      <MigrationStepper
        steps={[
          {
            key: "roster",
            label: "1. Employee roster",
            status: job.rosterStatus,
          },
          {
            key: "census",
            label: "2. Enrollment census",
            status: job.censusStatus,
          },
          { key: "plans", label: "3. Plans", status: job.plansStatus },
          {
            key: "enrollments",
            label: "4. Enrollments",
            status: job.enrollmentsStatus,
          },
          {
            key: "done",
            label: "Summary",
            status:
              job.enrollmentsStatus === "confirmed"
                ? ("confirmed" as MigrationStepStatus)
                : ("pending" as MigrationStepStatus),
          },
        ]}
        active={activeTab}
        onChange={(next) => setActiveTab(next)}
      />

      {activeTab === "roster" && (() => {
        const stagedEmployees =
          (job.rosterData as { employees?: any[] } | null)?.employees ?? [];
        const stagedDependents =
          (job.rosterData as { dependents?: any[] } | null)?.dependents ?? [];
        const rosterWarnings: ParserWarning[] = Array.isArray(
          job.rosterValidationErrors,
        )
          ? (job.rosterValidationErrors as ParserWarning[])
          : [];
        const hasStaged = stagedEmployees.length > 0 || stagedDependents.length > 0;

        return (
        <div className="mt-4 space-y-4">
          {/* ─── Step 1: Employee roster (skippable) ─── */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Step 1 — Employee roster</CardTitle>
                  <CardDescription>
                    Upload the <strong>Full Census</strong> export so part-time,
                    ineligible, and waived employees are captured before we
                    process enrollments. Skip if the roster is already loaded
                    for this client.
                  </CardDescription>
                </div>
                <StepStatusBadge status={job.rosterStatus} />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                <Users className="mr-1 inline h-3.5 w-3.5" />
                Current roster:{" "}
                <span className="font-medium text-foreground">
                  {currentRosterCount}
                </span>{" "}
                employee{currentRosterCount === 1 ? "" : "s"} on this client.
              </div>

              {/* Hidden file input shared by the drop-zone and Replace button */}
              <input
                ref={rosterFileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) rosterUpload.mutate(f);
                  e.target.value = "";
                }}
              />

              {!hasStaged && job.rosterStatus !== "confirmed" && job.rosterStatus !== "skipped" && (
                <>
                  <div
                    onClick={() =>
                      !rosterUpload.isPending && rosterFileInputRef.current?.click()
                    }
                    onDragOver={(e) => {
                      e.preventDefault();
                      setRosterDragOver(true);
                    }}
                    onDragLeave={() => setRosterDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setRosterDragOver(false);
                      const f = e.dataTransfer.files?.[0];
                      if (f && !rosterUpload.isPending) rosterUpload.mutate(f);
                    }}
                    className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 transition-colors ${
                      rosterDragOver
                        ? "border-blue-400 bg-blue-50 dark:bg-blue-900/20"
                        : "border-muted-foreground/20 hover:border-muted-foreground/40 bg-muted/20"
                    } ${rosterUpload.isPending ? "pointer-events-none opacity-60" : "cursor-pointer"}`}
                  >
                    {rosterUpload.isPending ? (
                      <>
                        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                        <span className="text-sm text-blue-600">Parsing file…</span>
                      </>
                    ) : (
                      <>
                        <Upload className="h-8 w-8 text-muted-foreground/40" />
                        <p className="text-sm font-medium">
                          Drop the Ease Full Census CSV here or click to browse
                        </p>
                        <p className="text-xs text-muted-foreground">
                          One row per person, with a Relationship column
                          distinguishing employees from dependents.
                        </p>
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      Or, if the roster is already loaded:
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => confirmRoster.mutate(true)}
                      disabled={confirmRoster.isPending}
                    >
                      {confirmRoster.isPending && (
                        <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                      )}
                      Skip — roster already loaded
                    </Button>
                  </div>
                </>
              )}

              {/* Parsed-but-not-confirmed: preview + confirm */}
              {hasStaged && job.rosterStatus !== "confirmed" && (
                <div className="space-y-4">
                  {job.rosterFileName && (
                    <FileHeaderCard
                      fileName={job.rosterFileName}
                      sourcePlatform={job.sourcePlatform}
                      formatLabel="Full Census"
                      columnCount={null}
                      rowCount={stagedEmployees.length + stagedDependents.length}
                      isReplacing={rosterUpload.isPending}
                      onReplace={(f) => rosterUpload.mutate(f)}
                    />
                  )}

                  {rosterWarnings.length > 0 && (
                    <WarningsPanel
                      title={`${rosterWarnings.length} parser warning${
                        rosterWarnings.length === 1 ? "" : "s"
                      }`}
                      warnings={rosterWarnings}
                    />
                  )}

                  {/* Upload-only step: no preview table here — Step 2 is the
                      single review surface. We just confirm the file parsed
                      cleanly and let the broker continue. */}
                  <div className="rounded-md border bg-emerald-50/50 p-4 dark:bg-emerald-950/20">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium">
                          {stagedEmployees.length} employee
                          {stagedEmployees.length === 1 ? "" : "s"}
                          {stagedDependents.length > 0
                            ? ` + ${stagedDependents.length} dependent${
                                stagedDependents.length === 1 ? "" : "s"
                              }`
                            : ""}{" "}
                          parsed
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          You'll review and edit each person on{" "}
                          <strong>Step 2</strong>. Anyone on the roster who
                          isn't in the enrollment census will appear in a
                          separate "Roster-only" section there.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => rosterFileInputRef.current?.click()}
                      disabled={rosterUpload.isPending}
                    >
                      <Upload className="mr-2 h-3.5 w-3.5" />
                      Replace file
                    </Button>
                  </div>
                </div>
              )}

              {job.rosterStatus === "confirmed" && (
                <div className="text-xs text-muted-foreground">
                  Roster confirmed. Continue to the enrollment census.
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        );
      })()}

      {activeTab === "census" && (
        <div className="mt-4 space-y-4">
      {/* File header (shown once Step 2's CSV is parsed) */}
      {job.censusFileName && (() => {
        const raw = (job.censusRawData ?? {}) as {
          headers?: string[];
          rawRows?: unknown[];
        };
        return (
          <FileHeaderCard
            fileName={job.censusFileName}
            sourcePlatform={job.sourcePlatform}
            formatLabel="Enrollment Census"
            columnCount={raw.headers?.length ?? null}
            rowCount={Array.isArray(raw.rawRows) ? raw.rawRows.length : null}
            isReplacing={upload.isPending}
            onReplace={(f) => upload.mutate(f)}
          />
        );
      })()}

      {/* Upload card (shown only when no file is parsed yet) */}
      {!job.censusFileName && (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" /> Upload enrollment census file
              </CardTitle>
              <CardDescription>
                Export your client group as the{" "}
                <strong>Enrollment Census (Vertical)</strong> CSV from{" "}
                {job.sourcePlatform} and upload it here. Roster-only employees
                (no current enrollments) can be added later via{" "}
                <EmployeeImport
                  clientId={job.clientId ?? 0}
                  renderTrigger={(onClick) => (
                    <button
                      type="button"
                      onClick={onClick}
                      disabled={!job.clientId}
                      className="underline underline-offset-2 hover:text-foreground disabled:opacity-50"
                    >
                      Employee Import
                    </button>
                  )}
                />
                .
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
              e.target.value = "";
            }}
          />
          <div
            onClick={() => !upload.isPending && fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOverTarget("upload");
            }}
            onDragLeave={() => setDragOverTarget(null)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOverTarget(null);
              const f = e.dataTransfer.files?.[0];
              if (f && !upload.isPending) upload.mutate(f);
            }}
            className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 transition-colors ${
              dragOverTarget === "upload"
                ? "border-blue-400 bg-blue-50 dark:bg-blue-900/20"
                : "border-muted-foreground/20 hover:border-muted-foreground/40 bg-muted/20"
            } ${upload.isPending ? "pointer-events-none opacity-60" : "cursor-pointer"}`}
          >
            {upload.isPending ? (
              <>
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                <span className="text-sm text-blue-600">Parsing file…</span>
              </>
            ) : (
              <>
                <FileSpreadsheet className="h-8 w-8 text-muted-foreground/40" />
                <p className="text-sm font-medium">
                  Drop your CSV file here or click to browse
                </p>
                <p className="text-xs text-muted-foreground">
                  CSV exports from Ease, up to 10MB
                </p>
              </>
            )}
          </div>
          {uploadSummary && (
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <div className="font-medium">File parsed</div>
              <div className="mt-1 grid grid-cols-3 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>Carriers: {uploadSummary.carriers}</span>
                <span>Plans: {uploadSummary.plans}</span>
                <span>Employees: {uploadSummary.employees}</span>
                <span>Dependents: {uploadSummary.dependents}</span>
                <span>Enrollments: {uploadSummary.enrollments}</span>
                <span>Warnings: {uploadSummary.warnings}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Blockers — gates this step's confirm */}
      {blockers.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>
            {blockers.length} employee
            {blockers.length === 1 ? "" : "s"} can't be imported as-is
          </AlertTitle>
          <AlertDescription className="space-y-2">
            <p>
              <strong>Missing email:</strong>{" "}
              {blockers
                .slice(0, 5)
                .map((b) => b.message.split(" has no email")[0])
                .join(", ")}
              {blockers.length > 5 && (
                <span className="text-muted-foreground">
                  {" "}
                  (+{blockers.length - 5} more)
                </span>
              )}
            </p>
            <div>
              <p className="font-medium">How to fix — pick one:</p>
              <ol className="ml-5 list-decimal space-y-1">
                <li>
                  Re-export your file using the{" "}
                  <strong>Enrollment Census (Vertical)</strong> template
                  (includes emails) and re-upload.
                </li>
                <li>
                  Edit each affected employee inline below using the ✏️
                  icon and add their email — the alert will update as you
                  go.
                </li>
              </ol>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Parser warnings for the enrollment census */}
      <WarningsPanel
        title={`${parserWarnings.length} parser warning${
          parserWarnings.length === 1 ? "" : "s"
        }`}
        warnings={parserWarnings}
        enrollments={(job?.enrollmentsData as any)?.enrollments}
      />

      {/* Column mapping for the enrollment census headers */}
      {(() => {
        const raw = (job.censusRawData ?? {}) as {
          headers?: string[];
          sampleRows?: Record<string, string>[];
        };
        const headers = raw.headers ?? [];
        const sampleRows = raw.sampleRows ?? [];
        const currentMapping = (job.censusColumnMapping ?? {}) as Record<
          string,
          string | null
        >;
        const fields: InternalField[] = job.internalFields ?? [];
        if (headers.length === 0 || fields.length === 0) return null;
        return (
          <ColumnMapper
            jobId={job.id}
            headers={headers}
            currentMapping={currentMapping}
            suggestedMapping={currentMapping}
            sampleRows={sampleRows}
            internalFields={fields}
            isConfirmed={
              job.censusStatus === "confirmed" ||
              job.plansStatus === "confirmed" ||
              job.enrollmentsStatus === "confirmed"
            }
          />
        );
      })()}

      {/* ─── Step 2: Enrollment census ─── */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Step 2 — Enrollment census</CardTitle>
              <CardDescription>
                Employees and dependents that will be created under this client.
              </CardDescription>
            </div>
            <StepStatusBadge status={job.censusStatus} />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Employees + nested dependents — from enrollment census */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium">
              From enrollment census · {census.employees?.length ?? 0}{" "}
              <span className="text-muted-foreground font-normal">
                · {census.dependents?.length ?? 0} dependents total
              </span>
            </h3>
            {(census.employees?.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">
                No employee rows parsed yet. Upload a file above.
              </p>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>SSN</TableHead>
                      <TableHead>DOB</TableHead>
                      <TableHead>Hire date</TableHead>
                      <TableHead>Sex</TableHead>
                      <TableHead>Comp</TableHead>
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(showAllEmployees
                      ? (census.employees ?? [])
                      : (census.employees ?? []).slice(0, PREVIEW_LIMIT)
                    ).flatMap((e: any, i: number) => {
                      const key = empKeyFromEmployee(e);
                      const deps = depsByEmployee.get(key) ?? [];
                      const isExpanded = expandedEmployees.has(key);
                      const rows = [
                        <TableRow
                          key={`emp-${i}`}
                          className={
                            deps.length > 0
                              ? "cursor-pointer hover:bg-muted/40"
                              : ""
                          }
                          onClick={() => {
                            if (deps.length === 0) return;
                            setExpandedEmployees((prev) => {
                              const next = new Set(prev);
                              if (next.has(key)) next.delete(key);
                              else next.add(key);
                              return next;
                            });
                          }}
                        >
                          <TableCell className="py-2">
                            {deps.length > 0 ? (
                              isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              )
                            ) : null}
                          </TableCell>
                          <TableCell className="font-medium">
                            <span>
                              {e.firstName}{" "}
                              {e.middleName ? `${e.middleName} ` : ""}
                              {e.lastName}
                            </span>
                            {deps.length > 0 && (
                              <Badge
                                variant="outline"
                                className="ml-2 gap-1 text-xs font-normal"
                              >
                                <Users className="h-3 w-3" />
                                {deps.length}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className={e.email ? "" : "text-red-600"}>
                            {fmt(e.email)}
                          </TableCell>
                          <TableCell>{maskSsn(e.ssn)}</TableCell>
                          <TableCell>{fmt(e.dateOfBirth)}</TableCell>
                          <TableCell>{fmt(e.hireDate)}</TableCell>
                          <TableCell>{fmt(e.sex)}</TableCell>
                          <TableCell>
                            {e.compensationAmount != null
                              ? `$${e.compensationAmount}`
                              : "—"}
                          </TableCell>
                          <TableCell className="text-right">
                            {!isWizardDone && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2"
                                title="View / edit details"
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  setEditingEmployeeIdx(i);
                                }}
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>,
                      ];
                      if (isExpanded && deps.length > 0) {
                        rows.push(
                          <TableRow
                            key={`emp-${i}-deps-header`}
                            className="bg-muted/30"
                          >
                            <TableCell></TableCell>
                            <TableCell className="py-1 text-xs font-medium text-muted-foreground">
                              Dependent
                            </TableCell>
                            <TableCell className="py-1 text-xs font-medium text-muted-foreground">
                              Relationship
                            </TableCell>
                            <TableCell className="py-1 text-xs font-medium text-muted-foreground">
                              SSN
                            </TableCell>
                            <TableCell className="py-1 text-xs font-medium text-muted-foreground">
                              DOB
                            </TableCell>
                            <TableCell className="py-1 text-xs font-medium text-muted-foreground">
                              Sex
                            </TableCell>
                            <TableCell colSpan={3}></TableCell>
                          </TableRow>,
                        );
                        deps.forEach((d: any, di: number) => {
                          rows.push(
                            <TableRow
                              key={`emp-${i}-dep-${di}`}
                              className="bg-muted/10"
                            >
                              <TableCell></TableCell>
                              <TableCell className="pl-6">
                                {d.firstName || d.lastName ? (
                                  <span>
                                    {d.firstName} {d.lastName}
                                  </span>
                                ) : (
                                  <span className="text-amber-600">
                                    name missing
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>{fmt(d.relationship)}</TableCell>
                              <TableCell>{maskSsn(d.ssn)}</TableCell>
                              <TableCell>{fmt(d.dateOfBirth)}</TableCell>
                              <TableCell>{fmt(d.sex)}</TableCell>
                              <TableCell colSpan={2}></TableCell>
                              <TableCell className="text-right">
                                {(() => {
                                  // Resolve the dep's index in the full
                                  // census.dependents array so the modal can
                                  // mutate the right entry.
                                  const allDeps = (census.dependents ??
                                    []) as any[];
                                  const depIdx = allDeps.indexOf(d);
                                  if (depIdx < 0 || isWizardDone) return null;
                                  return (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 px-2"
                                      title="View / edit dependent"
                                      onClick={(ev) => {
                                        ev.stopPropagation();
                                        setEditingDependentIdx(depIdx);
                                      }}
                                    >
                                      <Eye className="h-3.5 w-3.5" />
                                    </Button>
                                  );
                                })()}
                              </TableCell>
                            </TableRow>,
                          );
                        });
                      }
                      return rows;
                    })}
                  </TableBody>
                </Table>
                <PreviewToggle
                  total={census.employees?.length ?? 0}
                  shown={Math.min(
                    showAllEmployees
                      ? (census.employees?.length ?? 0)
                      : PREVIEW_LIMIT,
                    census.employees?.length ?? 0,
                  )}
                  expanded={showAllEmployees}
                  onToggle={() => setShowAllEmployees((v) => !v)}
                />
              </div>
            )}
          </div>

          {/* Roster-only people — uploaded in Step 1 but absent from the
              enrollment census. Edited via EditEmployeeModal in roster mode
              (writes back to rosterData.employees). */}
          {coverage.diffComputed && coverage.rosterOnly > 0 && (
            <RosterOnlyTable
              diffComputed
              rows={coverage.rosterOnlyPeople.map<RosterOnlyRow>(
                ({ rosterIdx, emp }) => {
                  const status = String(
                    emp.employmentStatus ?? "",
                  ).toLowerCase();
                  const empType = String(
                    emp.employmentType ?? "",
                  ).toLowerCase();
                  const why =
                    status === "terminated"
                      ? "Terminated"
                      : empType.includes("part") || empType.includes("pt")
                        ? "Part-time / not benefits eligible"
                        : "On roster but no enrollment record";
                  return {
                    rosterIdx,
                    firstName: emp.firstName ?? "",
                    lastName: emp.lastName ?? "",
                    email: emp.email ?? null,
                    hireDate: emp.hireDate ?? null,
                    whyRosterOnly: why,
                  };
                },
              )}
              onEdit={
                isWizardDone
                  ? undefined
                  : (rosterIdx) => setEditingRosterEmployeeIdx(rosterIdx)
              }
            />
          )}

          {/* Orphan dependents (rare — dep's employeeEid doesn't match any employee) */}
          {orphanDeps.length > 0 && (
            <Alert variant="destructive">
              <AlertTitle>
                {orphanDeps.length} unlinked dependent{orphanDeps.length > 1 ? "s" : ""}
              </AlertTitle>
              <AlertDescription>
                These dependents could not be matched to any employee in the
                census. They'll be skipped on confirm. Review the source file.
                <ul className="mt-2 list-disc pl-5 text-xs">
                  {orphanDeps.slice(0, 10).map((d: any, i: number) => (
                    <li key={i}>
                      {d.firstName} {d.lastName} ({d.relationship}) → tried
                      employee {d.employeeFirstName} {d.employeeLastName}
                    </li>
                  ))}
                  {orphanDeps.length > 10 && (
                    <li>... and {orphanDeps.length - 10} more</li>
                  )}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <ConfirmIssuesPanel step="census" result={confirmCensus.data} />
        </CardContent>
      </Card>
        </div>
      )}

      {activeTab === "plans" && (
        <div className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Step 3 — Plans &amp; rates</CardTitle>
                  <CardDescription>
                    Review and edit each plan before committing. Missing carrier
                    or effective date is highlighted in amber.
                  </CardDescription>
                </div>
                <StepStatusBadge status={job.plansStatus} />
              </div>
            </CardHeader>
            <CardContent>
              <PlansStep
                jobId={job.id}
                carriers={(plansBlob.carriers ?? []) as any}
                initialPlans={(plansBlob.plans ?? []) as any}
                parsedEnrollments={(enrollmentsBlob.enrollments ?? []) as any}
                isConfirmed={job.plansStatus === "confirmed"}
                canConfirm={canConfirmPlans}
                blockedReason={
                  job.censusStatus !== "confirmed"
                    ? "Confirm the census step first."
                    : null
                }
                onGateChange={setPlansGate}
                isLocked={isWizardDone}
                clientState={clientState}
                clientId={job.clientId ?? null}
              />
              <ConfirmIssuesPanel step="plans" result={confirmPlans.data} />
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === "enrollments" && (
        <div className="mt-4">
      {/* ─── Step 3: Enrollments ─── */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Step 4 — Enrollments</CardTitle>
              <CardDescription>
                Elections per person: coverage tier, waive reason, effective
                dates.
              </CardDescription>
            </div>
            <StepStatusBadge status={job.enrollmentsStatus} />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {(() => {
            const all = (enrollmentsBlob.enrollments ?? []) as any[];
            const counts = all.reduce<Record<string, number>>((acc, e) => {
              acc[e.status] = (acc[e.status] ?? 0) + 1;
              return acc;
            }, {});
            const empCount = all.filter((e) => !e.isDependent).length;
            const depCount = all.filter((e) => e.isDependent).length;
            const planNames = Array.from(
              new Set(
                all
                  .map((e) => String(e.planDisplayName ?? "").trim())
                  .filter(Boolean),
              ),
            ).sort();

            const search = enrollmentSearch.trim().toLowerCase();
            const filtered = all.filter((e) => {
              if (
                enrollmentStatusFilter &&
                e.status !== enrollmentStatusFilter
              )
                return false;
              if (enrollmentPersonFilter === "employee" && e.isDependent)
                return false;
              if (
                enrollmentPersonFilter === "dependent" &&
                !e.isDependent
              )
                return false;
              if (
                enrollmentPlanFilter !== "all" &&
                String(e.planDisplayName ?? "") !== enrollmentPlanFilter
              )
                return false;
              if (search) {
                const hay =
                  `${e.personFirstName ?? ""} ${e.personLastName ?? ""} ${e.ownerEmployeeFirstName ?? ""} ${e.ownerEmployeeLastName ?? ""}`.toLowerCase();
                if (!hay.includes(search)) return false;
              }
              return true;
            });

            const hasActiveFilter =
              enrollmentStatusFilter !== null ||
              enrollmentPersonFilter !== "all" ||
              enrollmentPlanFilter !== "all" ||
              enrollmentSearch.trim() !== "";

            return (
              <div className="space-y-3">
                {/* Status sub-tabs — matches ColumnMapper Mapped/Skipped/No-equiv pattern */}
                <Tabs
                  value={enrollmentStatusFilter ?? "all"}
                  onValueChange={(v) =>
                    setEnrollmentStatusFilter(
                      v === "all"
                        ? null
                        : (v as "enrolled" | "waived" | "terminated"),
                    )
                  }
                >
                  <TabsList className="w-full justify-start">
                    <TabsTrigger value="all" className="gap-2">
                      All
                      <Badge
                        variant="outline"
                        className="text-[10px] text-muted-foreground"
                      >
                        {all.length}
                      </Badge>
                    </TabsTrigger>
                    <TabsTrigger value="enrolled" className="gap-2">
                      Enrolled
                      <Badge
                        variant="outline"
                        className="border-emerald-300 bg-emerald-50 text-[10px] text-emerald-700"
                      >
                        {counts.enrolled ?? 0}
                      </Badge>
                    </TabsTrigger>
                    <TabsTrigger value="waived" className="gap-2">
                      Waived
                      <Badge
                        variant="outline"
                        className={`text-[10px] ${
                          (counts.waived ?? 0) > 0
                            ? "border-amber-300 bg-amber-50 text-amber-800"
                            : "text-muted-foreground"
                        }`}
                      >
                        {counts.waived ?? 0}
                      </Badge>
                    </TabsTrigger>
                    <TabsTrigger value="terminated" className="gap-2">
                      Terminated
                      <Badge
                        variant="outline"
                        className="text-[10px] text-muted-foreground"
                      >
                        {counts.terminated ?? 0}
                      </Badge>
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                {/* Secondary filters: person, plan, search */}
                <div className="flex flex-wrap items-center gap-2">
                  <Select
                    value={enrollmentPersonFilter}
                    onValueChange={(v) =>
                      setEnrollmentPersonFilter(
                        v as typeof enrollmentPersonFilter,
                      )
                    }
                  >
                    <SelectTrigger className="h-8 w-[170px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        All people · {all.length}
                      </SelectItem>
                      <SelectItem value="employee">
                        Employees · {empCount}
                      </SelectItem>
                      <SelectItem value="dependent">
                        Dependents · {depCount}
                      </SelectItem>
                    </SelectContent>
                  </Select>

                  <Select
                    value={enrollmentPlanFilter}
                    onValueChange={(v) => setEnrollmentPlanFilter(v)}
                  >
                    <SelectTrigger className="h-8 w-[240px] text-xs">
                      <SelectValue placeholder="All plans" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        All plans · {planNames.length}
                      </SelectItem>
                      {planNames.map((name) => (
                        <SelectItem key={name} value={name}>
                          {name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={enrollmentSearch}
                      onChange={(e) => setEnrollmentSearch(e.target.value)}
                      placeholder="Search name…"
                      className="h-8 w-[200px] pl-7 text-xs"
                    />
                  </div>

                  <div className="ml-auto flex items-center gap-2">
                    {hasActiveFilter && (
                      <>
                        <span className="text-xs text-muted-foreground">
                          {filtered.length} of {all.length}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 gap-1 text-xs"
                          onClick={() => {
                            setEnrollmentStatusFilter(null);
                            setEnrollmentPersonFilter("all");
                            setEnrollmentPlanFilter("all");
                            setEnrollmentSearch("");
                          }}
                        >
                          <X className="h-3 w-3" />
                          Clear
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {filtered.length > 0 ? (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Person</TableHead>
                          <TableHead>Rel.</TableHead>
                          <TableHead>Plan</TableHead>
                          <TableHead>Tier</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Effective</TableHead>
                          <TableHead className="w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(showAllEnrollments
                          ? filtered
                          : filtered.slice(0, PREVIEW_LIMIT)
                        ).map((e: any, i: number) => (
                          <TableRow key={i}>
                            <TableCell className="font-medium">
                              {e.personFirstName} {e.personLastName}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {e.relationship}
                            </TableCell>
                            <TableCell>{fmt(e.planDisplayName)}</TableCell>
                            <TableCell className="text-xs">
                              {fmt(e.coverageTier)}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={
                                  e.status === "enrolled"
                                    ? "text-green-700 border-green-300"
                                    : e.status === "waived"
                                      ? "text-amber-700 border-amber-300"
                                      : "text-muted-foreground"
                                }
                              >
                                {e.status}
                              </Badge>
                            </TableCell>
                            <TableCell>{fmt(e.effectiveDate)}</TableCell>
                            <TableCell className="text-right">
                              {!isWizardDone && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2"
                                  title="View / edit enrollment"
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    const idx = all.indexOf(e);
                                    if (idx >= 0)
                                      setEditingEnrollmentIdx(idx);
                                  }}
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <PreviewToggle
                      total={filtered.length}
                      shown={Math.min(
                        showAllEnrollments
                          ? filtered.length
                          : PREVIEW_LIMIT,
                        filtered.length,
                      )}
                      expanded={showAllEnrollments}
                      onToggle={() => setShowAllEnrollments((v) => !v)}
                    />
                  </div>
                ) : all.length > 0 ? (
                  <div className="rounded-md border border-dashed bg-muted/20 p-6 text-center text-sm text-muted-foreground">
                    No enrollments match the active filters.
                  </div>
                ) : null}
              </div>
            );
          })()}

          <ConfirmIssuesPanel
            step="enrollments"
            result={confirmEnrollments.data}
          />
        </CardContent>
      </Card>
        </div>
      )}

      {activeTab === "done" && (
        <div className="mt-4">
          <DoneStep jobId={jobId} />
        </div>
      )}

      {/* Hide footer on the Done step — it has its own action cards. */}
      {activeTab !== "done" && (() => {
        // Per-step gate. The footer Continue confirms-and-advances; this
        // computes whether it should be enabled and what tooltip to show
        // when blocked. Each step owns its own preconditions.
        const stagedRosterCount =
          ((job.rosterData as { employees?: unknown[] } | null)
            ?.employees ?? []).length;
        const rosterConfirmedOrSkipped =
          job.rosterStatus === "confirmed" || job.rosterStatus === "skipped";

        let footerDisabled = false;
        let footerReason = "";
        if (activeTab === "roster") {
          if (!rosterConfirmedOrSkipped && stagedRosterCount === 0) {
            footerDisabled = true;
            footerReason =
              "Upload the Full Census file or use 'Skip — roster already loaded'.";
          }
          if (confirmRoster.isPending) {
            footerDisabled = true;
            footerReason = "Confirming…";
          }
        } else if (activeTab === "census") {
          if (job.censusStatus !== "confirmed") {
            if (!canConfirmCensus) {
              footerDisabled = true;
              footerReason = "Upload an enrollment census file first.";
            } else if (blockers.length > 0) {
              footerDisabled = true;
              footerReason = `Resolve ${blockers.length} blocker${blockers.length === 1 ? "" : "s"} above before continuing.`;
            } else if (confirmCensus.isPending) {
              footerDisabled = true;
              footerReason = "Confirming…";
            }
          }
        } else if (activeTab === "plans") {
          if (job.plansStatus !== "confirmed") {
            if (!plansGate.ready) {
              footerDisabled = true;
              footerReason = plansGate.reason || "Plans not ready to confirm.";
            } else if (confirmPlans.isPending) {
              footerDisabled = true;
              footerReason = "Confirming…";
            }
          }
        } else if (activeTab === "enrollments") {
          if (job.enrollmentsStatus !== "confirmed") {
            if (!canConfirmEnrollments) {
              footerDisabled = true;
              footerReason = "Confirm the plans step first.";
            } else if (confirmEnrollments.isPending) {
              footerDisabled = true;
              footerReason = "Confirming…";
            }
          }
        }

        return (
        <StepFooter
          currentStep={activeTab}
          steps={[
            { key: "roster", status: job.rosterStatus },
            { key: "census", status: job.censusStatus },
            { key: "plans", status: job.plansStatus },
            { key: "enrollments", status: job.enrollmentsStatus },
          ]}
          disabled={footerDisabled}
          disabledReason={footerReason}
          continueLabel="Confirm & continue"
          onBack={() => {
            if (activeTab === "enrollments") setActiveTab("plans");
            else if (activeTab === "plans") setActiveTab("census");
            else if (activeTab === "census") setActiveTab("roster");
          }}
          onContinue={() => {
            if (activeTab === "roster") {
              if (rosterConfirmedOrSkipped) {
                setActiveTab("census");
                return;
              }
              if (stagedRosterCount > 0) confirmRoster.mutate(false);
              else confirmRoster.mutate(true);
            } else if (activeTab === "census") {
              if (job.censusStatus === "confirmed") setActiveTab("plans");
              else confirmCensus.mutate();
            } else if (activeTab === "plans") {
              if (job.plansStatus === "confirmed")
                setActiveTab("enrollments");
              else confirmPlans.mutate();
            } else if (activeTab === "enrollments") {
              if (job.enrollmentsStatus === "confirmed")
                setActiveTab("done");
              else confirmEnrollments.mutate();
            }
          }}
        />
        );
      })()}

      {editingEmployeeIdx !== null && (
        <EditEmployeeModal
          open={editingEmployeeIdx !== null}
          onOpenChange={(o) => {
            if (!o) setEditingEmployeeIdx(null);
          }}
          jobId={job.id}
          employees={(census.employees ?? []) as EditableEmployee[]}
          index={editingEmployeeIdx}
        />
      )}

      {editingRosterEmployeeIdx !== null && (
        <EditEmployeeModal
          open={editingRosterEmployeeIdx !== null}
          onOpenChange={(o) => {
            if (!o) setEditingRosterEmployeeIdx(null);
          }}
          jobId={job.id}
          employees={
            ((job.rosterData as { employees?: EditableEmployee[] } | null)
              ?.employees ?? []) as EditableEmployee[]
          }
          index={editingRosterEmployeeIdx}
          mode="roster"
        />
      )}

      {editingDependentIdx !== null && (
        <EditDependentModal
          open={editingDependentIdx !== null}
          onOpenChange={(o) => {
            if (!o) setEditingDependentIdx(null);
          }}
          jobId={job.id}
          dependents={(census.dependents ?? []) as EditableDependent[]}
          index={editingDependentIdx}
        />
      )}

      {editingEnrollmentIdx !== null && (
        <EditEnrollmentModal
          open={editingEnrollmentIdx !== null}
          onOpenChange={(o) => {
            if (!o) setEditingEnrollmentIdx(null);
          }}
          jobId={job.id}
          enrollments={
            (enrollmentsBlob.enrollments ?? []) as EditableEnrollment[]
          }
          index={editingEnrollmentIdx}
        />
      )}

      {job.status === "complete" && (
        <Alert className="border-green-200 bg-green-50 dark:bg-green-900/20">
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>Migration complete</AlertTitle>
          <AlertDescription>
            All three steps are confirmed. The client's data is now in
            Administr.{" "}
            <Link href={`/clients/${job.clientId}`} className="underline">
              Open client →
            </Link>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
